import React, { useMemo, useCallback, useEffect, useState, useRef } from 'react';
import { XMLGroupModel } from '@/models/XMLGroup';
import { Vehicle, VehicleRowData, VehicleSortKey } from '@/types';
import { VehicleTable } from './VehicleTable';
import { VehicleSelector } from './VehicleSelector';
import { useXMLOperations } from '@/hooks/useXMLOperations';
import { useAppContext } from '@/context/AppContext';

interface GroupItemProps {
  group: XMLGroupModel;
  vehicleData: Map<string, Vehicle>;
  groupType: 'vehGroups' | 'pedGroups';
  groupIndex: number;
}

const GroupItemComponent: React.FC<GroupItemProps> = ({ group, vehicleData, groupType, groupIndex }) => {
  const { state } = useAppContext();
  const xmlOperations = useXMLOperations();
  const [currentModels, setCurrentModels] = useState<string[]>(group.models);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selectedVehicles, setSelectedVehicles] = useState<Set<string>>(new Set());
  const [sortKey, setSortKey] = useState<VehicleSortKey>('model');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  const contentRef = useRef<HTMLDivElement>(null);
  const groupId = `${groupType}-${groupIndex}-${group.name}`;
  
  // Load collapse state from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem(`group-collapsed-${groupId}`);
    if (savedState !== null) {
      setIsCollapsed(savedState === 'true');
    }
  }, [groupId]);
  
  // Save collapse state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(`group-collapsed-${groupId}`, isCollapsed.toString());
  }, [groupId, isCollapsed]);
  
  // Update models when XML document changes
  useEffect(() => {
    group.refreshModels();
    setCurrentModels([...group.models]);
    // Clear selection when models change
    setSelectedVehicles(new Set());
  }, [state.lastUpdate, group]);
  
  // Handle global collapse all trigger
  useEffect(() => {
    if (state.ui.globalCollapseTrigger > 0) {
      setIsCollapsed(true);
      localStorage.setItem(`group-collapsed-${groupId}`, 'true');
    }
    
  }, [state.ui.globalCollapseTrigger, groupId]);
  
  // Handle global expand all trigger
  useEffect(() => {
    if (state.ui.globalExpandTrigger > 0) {
      setIsCollapsed(false);
      localStorage.setItem(`group-collapsed-${groupId}`, 'false');
    }
  }, [state.ui.globalExpandTrigger, groupId]);
  
  // Handle smooth collapse/expand animation
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    
    const height = content.scrollHeight;
    const duration = Math.max(0.05, height / 10000); // 10000px per second, minimum 0.05s
    content.style.transition = `max-height ${duration}s linear`;
    
    if (isCollapsed) {
      // Collapsing: set to current height, then animate to 0
      content.style.maxHeight = height + 'px';
      // Force reflow
      content.offsetHeight;
      content.style.maxHeight = '0px';
    } else {
      // Expanding: animate to full height
      content.style.maxHeight = height + 'px';
    }
  }, [isCollapsed]);

  useEffect(() => {
    const content = contentRef.current;
    if (!content || isCollapsed) return;

    const updateHeight = () => {
      const height = content.scrollHeight;
      content.style.maxHeight = `${height}px`;
    };

    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(content);

    return () => resizeObserver.disconnect();
  }, [isCollapsed, currentModels.length]);
  
  // Prepare vehicle data for the table
  const vehicleRowData = useMemo(() => {
    const duplicates = group.checkForDuplicates();
    
    return currentModels.map(modelName => ({
      modelName,
      vehicle: vehicleData.get(modelName),
      isDuplicate: duplicates.includes(modelName)
    }));
  }, [currentModels, vehicleData, group]);

  const sortedVehicleRowData = useMemo(() => {
    const toComparableText = (value?: string) => value?.toLowerCase() || '';
    const compareText = (a?: string, b?: string) => {
      const aValue = toComparableText(a);
      const bValue = toComparableText(b);
      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;
      const result = aValue.localeCompare(bValue);
      return sortDirection === 'asc' ? result : -result;
    };

    const compareNumber = (a?: number, b?: number) => {
      const aValue = typeof a === 'number' ? a : null;
      const bValue = typeof b === 'number' ? b : null;
      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;
      const result = aValue - bValue;
      return sortDirection === 'asc' ? result : -result;
    };

    const getSortValue = (row: VehicleRowData) => {
      switch (sortKey) {
        case 'brand':
          return row.vehicle?.brand;
        case 'name':
          return row.vehicle?.name;
        case 'category':
          return row.vehicle?.category;
        case 'price':
          return row.vehicle?.price;
        case 'type':
          return row.vehicle?.type;
        default:
          return row.modelName;
      }
    };

    const sorted = [...vehicleRowData].sort((a, b) => {
      let result = 0;
      if (sortKey === 'price') {
        result = compareNumber(getSortValue(a) as number | undefined, getSortValue(b) as number | undefined);
      } else {
        result = compareText(getSortValue(a) as string | undefined, getSortValue(b) as string | undefined);
      }

      if (result === 0) {
        result = compareText(a.modelName, b.modelName);
      }

      return result;
    });

    return sorted;
  }, [vehicleRowData, sortKey, sortDirection]);

  const handleRemoveVehicle = useCallback((modelName: string) => {
    xmlOperations.removeVehicleFromGroup(group.element, modelName);
    // Immediately update local state for responsive UI
    group.refreshModels();
    setCurrentModels([...group.models]);
    // Remove from selection if selected
    setSelectedVehicles(prev => {
      const newSelection = new Set(prev);
      newSelection.delete(modelName);
      return newSelection;
    });
  }, [xmlOperations, group]);

  const handleSelectionChange = useCallback((modelName: string, selected: boolean) => {
    setSelectedVehicles(prev => {
      const newSelection = new Set(prev);
      if (selected) {
        newSelection.add(modelName);
      } else {
        newSelection.delete(modelName);
      }
      return newSelection;
    });
  }, []);

  const handleSelectAll = useCallback((selected: boolean) => {
    if (selected) {
      setSelectedVehicles(new Set(currentModels));
    } else {
      setSelectedVehicles(new Set());
    }
  }, [currentModels]);

  const handleSortChange = useCallback((nextSortKey: VehicleSortKey) => {
    setSortDirection(prevDirection => {
      if (nextSortKey === sortKey) {
        return prevDirection === 'asc' ? 'desc' : 'asc';
      }
      return 'asc';
    });
    setSortKey(nextSortKey);
  }, [sortKey]);

  const handleBulkRemove = useCallback(() => {
    if (selectedVehicles.size === 0) return;
    
    // Remove all selected vehicles
    selectedVehicles.forEach(modelName => {
      xmlOperations.removeVehicleFromGroup(group.element, modelName);
    });
    
    // Update local state
    group.refreshModels();
    setCurrentModels([...group.models]);
    setSelectedVehicles(new Set());
  }, [selectedVehicles, xmlOperations, group]);

  const handleToggleCollapse = useCallback(() => {
    setIsCollapsed((prev: boolean) => !prev);
  }, []);

  return (
    <div className={`group-item ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="group-header">
        <button 
          className="collapse-toggle"
          onClick={handleToggleCollapse}
          aria-expanded={!isCollapsed}
          aria-label={isCollapsed ? 'Expand group' : 'Collapse group'}
        >
          <span className={`collapse-icon ${isCollapsed ? 'collapsed' : ''}`}>▶</span>
        </button>
        <h3>Name: {group.name || 'Unknown'}</h3>
        <span className="group-model-count">
          ({currentModels.length} {currentModels.length === 1 ? 'model' : 'models'})
        </span>
        {selectedVehicles.size > 0 && (
          <button 
            className="bulk-remove-btn"
            onClick={handleBulkRemove}
            title={`Remove ${selectedVehicles.size} selected vehicles`}
          >
            Remove Selected ({selectedVehicles.size})
          </button>
        )}
      </div>

      <div className={`group-content ${isCollapsed ? 'hidden' : ''}`} ref={contentRef}>
        <div className="models-section">
          <strong>Models:</strong>
          
          {vehicleRowData.length > 0 ? (
            <VehicleTable
              vehicles={sortedVehicleRowData}
              onRemoveVehicle={handleRemoveVehicle}
              groupId={groupId}
              selectedVehicles={selectedVehicles}
              onSelectionChange={handleSelectionChange}
              onSelectAll={handleSelectAll}
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSortChange={handleSortChange}
            />
          ) : (
            <p className="no-vehicles">No vehicles in this group.</p>
          )}
        </div>

        <VehicleSelector
          groupType={groupType}
          existingVehicles={currentModels}
          groupElement={group.element}
          groupId={groupId}
        />
      </div>
    </div>
  );
};

export const GroupItem = React.memo(GroupItemComponent);
GroupItem.displayName = 'GroupItem';
